import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  FileText, Download, FileSpreadsheet, Calendar,
  Users, ArrowRight, AlertTriangle
} from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import SegmentedControl from '../ui/SegmentedControl';
import Dropdown from '../ui/Dropdown';
import PeriodNav from '../ui/PeriodNav';
import { pivotPublishedData } from '../../services/rosterPivoter';
import { isEmployeeUnavailable } from '../../services/scheduleService';
import { freeStaff, postCallStaff } from '../../services/availabilityRows';
import { computeStaffCounters } from '../../services/staffCounters';
import { groupStaff } from '../../services/staffGrouping';
import { indexCrossCovers } from '../../services/crossCover';
import { compareLocations } from '../../services/locationOrder';
import { apiFetch } from '../../services/apiClient';
import { INSTITUTIONS } from '../../services/staffFields';
import { siteOf, isTheatre } from '../../services/locationSites';
import { slotLocationName } from '../../services/locations';
import { rolesOf, roleName } from '../../services/onCallRoster';
import { leaveOf, onLeave, leaveLabel as leaveLabelOf } from '../../services/leave';
import { idOf } from '../../services/people';
import PageHeader from '../ui/PageHeader';
import ControlGroup from '../ui/ControlGroup';
import { ControlBar, ToggleGroup, SearchBox, EndGroup } from '../ui/ControlBar';

// Module scope, not component scope: this used to be re-created on every
// render, which made the INSTITUTIONS memo depend on a new array each time.
const KNOWN_INSTITUTIONS = INSTITUTIONS;

const FinalRosterTab = ({ schedule, exportToExcel, exportToPDF, onViewConsole }) => {
  const [groupBy, setGroupBy] = useState('date');
  // Global display metric for the staff-view workload counter ('shifts'|'hours').
  const [counterMetric, setCounterMetric] = useState('shifts');
  // Global staff grouping for the staff view: 'rank' (flat A-Z) or 'ward'
  // (by anchor location). Server-sourced so dashboard, final roster, and Excel
  // stay in step (mirrors staff_counter_metric).
  const [staffGroupBy, setStaffGroupBy] = useState('rank');
  // Count leave into the workload counter, per metric. Each metric carries its
  // own toggle and value, so leave can count in hours but not in shifts.
  // Display only: the solver reads leave the opposite way (see
  // DataService.get_leave_counter_settings), so this never changes a roster.
  const [leaveCounter, setLeaveCounter] = useState({
    shifts: { enabled: false, value: 1 },
    hours: { enabled: false, value: 8 },
  });
  useEffect(() => {
    apiFetch('/api/solver-settings')
      .then(r => r.json())
      .then(d => {
        if (d && d.staff_counter_metric) setCounterMetric(d.staff_counter_metric);
        if (d && d.staff_group_by) setStaffGroupBy(d.staff_group_by);
        if (d && d.leave_counter) setLeaveCounter(d.leave_counter);
      })
      .catch(() => {});
  }, []);
  // Persist the leave toggle for the CURRENTLY DISPLAYED metric, then reflect it
  // locally — the same round trip as changeStaffGroupBy, so the Excel export
  // (which reads the setting server-side) agrees with what is on screen.
  const toggleLeaveForMetric = () => {
    const cfg = leaveCounter[counterMetric];
    if (!cfg) return;
    const next = !cfg.enabled;
    setLeaveCounter({ ...leaveCounter, [counterMetric]: { ...cfg, enabled: next } });
    apiFetch('/api/leave-counter', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ metric: counterMetric, enabled: next }),
    }).catch(() => {});
  };
  // What one leave day is worth under a metric, or 0 when that metric's toggle
  // is off. 0 is the "ignore leave" signal computeStaffCounters reads.
  const leaveValueFor = (metric) => {
    const cfg = leaveCounter[metric];
    return cfg && cfg.enabled ? Number(cfg.value) || 0 : 0;
  };
  // Persist a staff-grouping change globally, then reflect it locally.
  const changeStaffGroupBy = (mode) => {
    setStaffGroupBy(mode);
    apiFetch('/api/staff-group-by', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ staff_group_by: mode }),
    }).catch(() => {});
  };
  // Rank-0 = most senior (Consultant / Team Lead, whatever it's named). Keyed on
  // the numeric rank so the seniority highlight survives rank RENAMES -- reading
  // the title string broke the moment a site renamed rank 0 (e.g. to "Team Lead").
  const rank0 = (emp) => {
    if (!emp) return false;
    const r = (emp.rankOverride ?? emp.rank_override ?? emp.rank);
    return r === 0 || r === '0';
  };

  // Weekend column tinting. Sat/Sun read as a distinct column in BOTH views.
  // Header is a step darker than the body so the column has a clear top edge
  // while the cell content underneath stays legible.
  //
  // The per-row semantic tints (emerald = on-call, rose = leave) are only
  // 5-30% opacity, so on weekends the grey takes precedence. The row's coloured
  // label cell on the left still identifies what each row is, so nothing is lost.
  const isWeekendISO = (dateStr) => {
    const d = new Date(dateStr);
    return d.getDay() === 0 || d.getDay() === 6;
  };
  const WEEKEND_HEADER = 'bg-slate-200';
  const WEEKEND_CELL = 'bg-slate-100';

  // Used to decide whether a person is genuinely OFF on a date or merely on
  // leave — a distinction the roster makes explicitly, so getting it wrong
  // would paint OFF over someone's leave. Any leave that day, half days too.
  const isOnLeaveByBlocks = (emp, dateStr) => onLeave(emp, dateStr);

  // Departments fall into two groups: those that run on-call and those that do
  // not. Show the on-call band only when somebody is actually ON call.
  //
  // Deliberately keyed on the WHOLE roster rather than the visible week, so the
  // row does not appear and vanish as you page between weeks -- it is a property
  // of the department, not of one week. Note that a department can have on-call
  // SLOTS generated with nobody assigned to them (that is the current state of
  // the Inpatient dept: 124 slots, 0 assigned), so counting slots is not enough
  // -- we require an actual employee.
  // main's HRM layout: FREE (per half-day) and POST-CALL rows replace OFF.
  const showFreeRows = Boolean(schedule?.rosterFreeRows ?? schedule?.roster_free_rows);
  const hasOnCall = useMemo(() => {
    const roster = (schedule?.onCallRoster || schedule?.on_call_roster) || [];
    return roster.some(s => s && s.employee);
  }, [schedule]);
  const [searchQuery, setSearchText] = useState('');
  const [weekOffset, setWeekOffset] = useState(0);
  // Roster window scope: 'week' (7 days) or 'fortnight' (14 days). Both the
  // calendar and staff views map over weekDates, so widening it here widens
  // both. Calendar-snapped (Mon start) and steps by the whole span so nav
  // moves a full week/fortnight.
  const [rosterScope, setRosterScope] = useState('week');

  // Shift-type code resolution. Items from rosterPivoter spread the original
  // shift, so shiftTypeId survives -- resolve the real code ('AM'|'PM'|'N'|...)
  // from schedule.shiftTypes rather than inferring from the hour, which would
  // file a 22:00 night as 'PM'.
  const shiftTypesById = useMemo(() => {
    const map = new Map();
    ((schedule?.shiftTypes || schedule?.shift_types) || []).forEach(t => {
      if (t && t.id) map.set(String(t.id), t);
    });
    return map;
  }, [schedule]);

  const codeOf = useMemo(() => (item) => {
    const stid = item?.shiftTypeId || item?.shift_type_id;
    if (stid && shiftTypesById.has(String(stid))) {
      const t = shiftTypesById.get(String(stid));
      return String(t.shortCode || t.short_code || t.id).trim().toUpperCase();
    }
    return new Date(item.start).getHours() < 12 ? 'AM' : 'PM';
  }, [shiftTypesById]);

  // AM/PM half-day bucket, for leave lookups.
  const bucketOf = (item) => (new Date(item.start).getHours() < 12 ? 'AM' : 'PM');

  // Codes to show, ordered by each type's start time then code. Falls back to
  // AM/PM when no types are defined.
  const orderedCodes = useMemo(() => {
    const types = [...((schedule?.shiftTypes || schedule?.shift_types) || [])]
      .filter(t => (t?.isActive ?? t?.is_active) !== false)
      .sort((a, b) => String(a.startTime ?? a.start_time ?? '99:99')
        .localeCompare(String(b.startTime ?? b.start_time ?? '99:99')));
    const codes = types.map(t => String(t.shortCode ?? t.short_code ?? t.id).toUpperCase());
    return codes.length ? [...new Set(codes)] : ['AM', 'PM'];
  }, [schedule]);

  // --- TRANSFORMATION ---
  const pivotedData = useMemo(() => {
    return pivotPublishedData(schedule, { groupBy });
  }, [schedule, groupBy]);

  // Which shifts are one person covering two wards in the same hours (derived
  // from the shifts themselves — see services/crossCover.js). Used to tag rows.
  const crossCoverIndex = useMemo(() => indexCrossCovers(schedule?.shifts || []), [schedule]);

  // Date-keyed pivot kept regardless of the selected view, so per-day headcounts
  // (calendar AND staff view) read the same source. Cheap: one pass over shifts.
  const datePivot = useMemo(() => pivotPublishedData(schedule, { groupBy: 'date' }), [schedule]);

  // Ward coverage ratio (IP #5): for a given date + shift code, how many wards
  // are STAFFED out of how many are IN OPERATION that day/shift.
  //   numerator   = distinct wards with a FILLED shift of this code this day
  //   denominator = distinct wards with a shift of this code this day at all
  //                 (staffed or not) — an unstaffed slot still means the ward is
  //                 operating that shift. Derived from schedule.shifts directly
  //                 (which includes unfilled slots), so it does not depend on
  //                 location_schedules reaching the frontend and stays consistent
  //                 with the numerator's source.
  // Generalises across shift bands; for a one-shift-per-day department it is just
  // the single band's ratio. A weekend-closed ward has no weekend slot, so it
  // never appears in a weekend denominator.
  //
  // day/code index over ALL shifts (filled + unfilled): dateISO -> code ->
  // { op:Set(loc), staffed:Set(loc) }.
  const wardCoverageIndex = useMemo(() => {
    const idx = {};
    ((schedule?.shifts) || []).forEach(s => {
      if (!s || !s.location) return;
      const dt = s.start ? String(s.start).split('T')[0] : null;
      if (!dt) return;
      const code = codeOf(s);
      const day = (idx[dt] || (idx[dt] = {}));
      const bucket = (day[code] || (day[code] = { op: new Set(), staffed: new Set() }));
      bucket.op.add(s.location);
      if (s.employee) bucket.staffed.add(s.location);
    });
    return idx;
  }, [schedule, codeOf]);

  const wardCoverageFor = (dateStr, code) => {
    const bucket = wardCoverageIndex[dateStr]?.[code];
    if (!bucket) return { staffed: 0, operating: 0 };
    return { staffed: bucket.staffed.size, operating: bucket.op.size };
  };

  const weekDates = useMemo(() => {
    const span = rosterScope === 'fortnight' ? 14 : 7;
    const today = new Date();
    const monday = new Date(today);
    // Monday of the target window. weekOffset counts WEEKS (the navigator
    // steps by 2 in fortnight mode), so switching Week<->Fortnight keeps the
    // SAME anchor Monday and merely extends/truncates the window — switching
    // span no longer teleports the view.
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1) + (weekOffset * 7);
    monday.setDate(diff);
    monday.setHours(0, 0, 0, 0);

    return Array.from({ length: span }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      // Use local date parts to avoid UTC timezone shifts back to Sunday
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const dayStr = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${dayStr}`;
    });
  }, [weekOffset, rosterScope]);

  // Date objects for the period label (local parts — never new Date('YYYY-MM-DD'),
  // which parses as UTC and can shift back a day).
  const weekRange = useMemo(() => {
    if (weekDates.length === 0) return null;
    const parse = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
    return { start: parse(weekDates[0]), end: parse(weekDates[weekDates.length - 1]) };
  }, [weekDates]);

  // --- LOGIC HELPERS ---
  // The location's site as set in its drawer, else the one its name names.
  const getInstitution = useCallback(
    (location) => (location && siteOf(location, schedule)) || 'OTHERS', [schedule]);

  const getInstTextColor = (inst) => {
    const map = {
      'NUH': 'text-blue-600',
      'NTFGH': 'text-orange-600',
      'AH': 'text-purple-600',
      'JMC': 'text-rose-600',
      'OTHERS': 'text-slate-500'
    };
    return map[inst] || map['OTHERS'];
  };

  const getProcessedDay = (dateStr, code) => {
    const shifts = (pivotedData[dateStr]?.clinical || []).filter(s => codeOf(s) === code);

    const groups = { 'NUH': { ot: [], clinic: [] }, 'NTFGH': { ot: [], clinic: [] }, 'AH': { ot: [], clinic: [] }, 'JMC': { ot: [], clinic: [] }, 'OTHERS': { ot: [], clinic: [] } };
    
    shifts.forEach(s => {
      const inst = getInstitution(s.location);
      const isOT = isTheatre(s.location, schedule);
      if (isOT) groups[inst].ot.push(s);
      else groups[inst].clinic.push(s);
    });

    // Clinics in the user-defined order (Clinical Operations → Reorder).
    const byLoc = compareLocations(schedule);
    Object.keys(groups).forEach(inst => {
      groups[inst].ot.sort((a, b) => byLoc(a.location, b.location));
      groups[inst].clinic.sort((a, b) => byLoc(a.location, b.location));
    });

    return groups;
  };

  // Daily headcount: number of UNIQUE staff working clinical shifts on a date.
  // Deduped by staff id, so somebody working two shifts in a day (AM + PM, or a
  // cross-cover) is counted once. On-call is a separate duty with its own band
  // and is not included here.
  const uniqueStaffOnDate = (dateStr) => {
    const ids = new Set();
    (datePivot[dateStr]?.clinical || []).forEach(s => {
      const id = idOf(s.employee);
      if (id) ids.add(id);
    });
    return ids.size;
  };

  const renderShiftItem = (s, inst) => {
    const isConsultant = rank0(s.employee);
    const dateObj = new Date(s.start);
    const isConflict = s.employee && isEmployeeUnavailable(schedule, s.employee, dateObj, bucketOf(s));
    const isCross = !!crossCoverIndex[s.id];
    const code = codeOf(s);

    return (
      <div key={s.id} className={`flex items-center justify-between px-3 py-1.5 border-b border-slate-50 last:border-0 transition-colors ${isConsultant ? 'bg-[#042f2e] text-white' : (isConflict ? 'bg-rose-50 hover:bg-rose-100/50' : (isCross ? 'bg-amber-50/60 hover:bg-amber-50' : 'hover:bg-slate-50'))}`}>
        <div className="flex flex-col items-start w-1/2 min-w-0">
           <div className="flex items-center gap-1 min-w-0 w-full">
             {isConflict && <AlertTriangle className="w-2.5 h-2.5 text-rose-500 flex-shrink-0" />}
             {/* Point 5: shift CODE is the prominent element, ward location demoted below. */}
             <span className={`text-[11px] font-black uppercase tracking-tight truncate leading-none ${isConsultant ? 'text-white' : (isConflict ? 'text-rose-700' : 'text-slate-800')}`}>{code}</span>
           </div>
           <span className={`text-[7px] font-bold uppercase tracking-tighter truncate leading-none mt-0.5 w-full ${isConsultant ? 'text-teal-100/60' : 'text-slate-400'}`}>{slotLocationName(s)}</span>
        </div>
        <div className="flex flex-col items-end w-1/2 min-w-0">
          <span className={`text-[10px] font-bold text-right leading-none truncate w-full ${isConsultant ? 'text-teal-100/90' : (isConflict ? 'text-rose-600' : 'text-slate-600')}`}>{s.employee?.name}</span>
          {(s.assignmentLabel || s.assignment_label) && (
            <span className={`text-[8px] font-bold leading-none truncate w-full text-right mt-0.5 ${isConsultant ? 'text-teal-200/80' : 'text-teal-600'}`}>{s.assignmentLabel || s.assignment_label}</span>
          )}
          {isCross && (
            <span className={`text-[7px] font-black uppercase tracking-wider leading-none truncate w-full text-right mt-0.5 ${isConsultant ? 'text-amber-200' : 'text-amber-700'}`} title="Cross-cover: this person is also covering another ward in the same hours">⚠ cross-cover</span>
          )}
        </div>
      </div>
    );
  };

const INSTITUTIONS = useMemo(() => {
  const present = new Set();
  Object.values(pivotedData || {}).forEach(day => {
    (day.clinical || []).forEach(s => present.add(getInstitution(s.location)));
  });
  const known = KNOWN_INSTITUTIONS.filter(i => present.has(i));
  const extras = [...present].filter(i => !KNOWN_INSTITUTIONS.includes(i)).sort();
  const list = [...known, ...extras];
  return list.length ? list : KNOWN_INSTITUTIONS;
}, [pivotedData, getInstitution]);

  const renderWeeklyLedger = () => {
    return (
      <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse table-fixed min-w-[1400px]">
            <thead>
              <tr className="bg-white border-b border-slate-200">
                <th className="w-24 p-4 border-r border-slate-100"></th>
                {weekDates.map(dateStr => {
                  const d = new Date(dateStr);
                  const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                  return (
                    <th key={dateStr} className={`p-4 text-center border-r border-slate-100 last:border-r-0 ${isWeekend ? WEEKEND_HEADER : ''}`}>
                      <div className="flex flex-col items-center">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                          {d.toLocaleDateString('en-US', { weekday: 'short' })}
                        </span>
                        <span className="text-xl font-bold text-slate-900 tracking-tighter">
                          {d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                        </span>
                        {/* Daily total: unique staff working clinical shifts that day. */}
                        {uniqueStaffOnDate(dateStr) > 0 && (
                          <span className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold tabular-nums text-teal-700 bg-teal-50 border border-teal-100 rounded-full px-2 py-0.5" title="Total staff working this day (unique headcount)">
                            <Users className="w-2.5 h-2.5" />{uniqueStaffOnDate(dateStr)}
                          </span>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {(() => {
                // One band per shift code that appears this week. Codes with no
                // shifts in the visible range are skipped, so a clinic-only week
                // shows AM/PM and an ED week adds an N band -- no empty bands.
                const bandLabels = { AM: 'Morning', PM: 'Afternoon', N: 'Night' };
                const activeCodes = orderedCodes.filter(code =>
                  weekDates.some(dateStr => {
                    const g = getProcessedDay(dateStr, code);
                    return INSTITUTIONS.some(inst => g[inst].ot.length || g[inst].clinic.length);
                  }));
                const bands = activeCodes.length ? activeCodes : ['AM', 'PM'];
                return bands.map((code, bandIdx) => (
                  <React.Fragment key={code}>
                    {bandIdx > 0 && (
                      <tr className="bg-slate-100/50 h-8"><td colSpan={weekDates.length + 1} className="px-4 py-1"><div className="h-px bg-slate-200 w-full"></div></td></tr>
                    )}
                    {/* Ward coverage ratio per date for THIS shift band (IP #5):
                        staffed wards / wards in operation that day. Rendered once
                        per band (crosses institutions), above the institution rows. */}
                    <tr className="bg-slate-50/40 border-b border-slate-100">
                      <td className="border-r border-slate-100 bg-slate-50/30 px-2 py-1 text-center">
                        <span className="text-[7px] font-bold uppercase tracking-widest text-slate-400">Wards</span>
                      </td>
                      {weekDates.map(dateStr => {
                        const { staffed, operating } = wardCoverageFor(dateStr, code);
                        if (operating === 0) {
                          return <td key={dateStr} className="border-r border-slate-50 last:border-r-0 text-center px-2 py-1"><span className="text-[9px] text-slate-300">—</span></td>;
                        }
                        const full = staffed >= operating;
                        return (
                          <td key={dateStr} className="border-r border-slate-50 last:border-r-0 text-center px-2 py-1">
                            <span
                              className={`inline-flex items-center gap-1 text-[10px] font-bold tabular-nums rounded-full px-2 py-0.5 border ${full ? 'text-teal-700 bg-teal-50 border-teal-100' : 'text-amber-700 bg-amber-50 border-amber-200'}`}
                              title={`${staffed} of ${operating} operating wards staffed for ${bandLabels[code] || code}`}
                            >
                              {staffed}/{operating}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                    {INSTITUTIONS.map((inst, instIdx) => (
                      <tr key={`${inst}-${code}`} className={`border-b border-slate-50 last:border-b-slate-200 ${instIdx === 0 && bandIdx > 0 ? 'print-page-break' : ''}`}>
                        {instIdx === 0 && (
                          <td rowSpan={INSTITUTIONS.length} className="border-r border-slate-100 bg-slate-50/30 p-4 align-middle text-center relative">
                            <span className={`text-[10px] font-bold uppercase tracking-[0.2em] [writing-mode:vertical-lr] rotate-180 ${code === 'AM' || code === 'PM' ? 'text-slate-400' : 'text-teal-500'}`}>{bandLabels[code] || code}</span>
                          </td>
                        )}
                        {weekDates.map(dateStr => {
                          const data = getProcessedDay(dateStr, code)[inst];
                          const hasShifts = data.ot.length > 0 || data.clinic.length > 0;
                          return (
                            <td key={dateStr} className={`p-2 border-r border-slate-50 align-top last:border-r-0 ${isWeekendISO(dateStr) ? WEEKEND_CELL : (!hasShifts ? 'bg-slate-50/10' : '')}`}>
                              {hasShifts && (
                                <div className="flex flex-col rounded-md overflow-hidden border border-slate-100 bg-white shadow-sm">
                                  <div className="px-3 py-1 bg-slate-50/50 border-b border-slate-50 flex items-center justify-between gap-2">
                                    <span className={`text-[8px] font-bold uppercase tracking-widest ${getInstTextColor(inst)}`}>{inst}</span>
                                    {/* Headcount: number of people working this shift group
                                        (same "who is working this shift" count as the
                                        management console). Published roster shows filled
                                        assignments, so this is the head count of people. */}
                                    <span className="text-[10px] font-bold tabular-nums text-slate-600 bg-slate-100 rounded px-1.5 py-0.5 flex items-center gap-0.5 flex-shrink-0" title="Headcount — unique people working this shift (a cross-cover counts once)">
                                      <Users className="w-2.5 h-2.5" />{new Set([...data.ot, ...data.clinic].map(s => s.employee?.name).filter(Boolean)).size}
                                    </span>
                                  </div>
                                  <div className="flex flex-col">
                                    {data.ot.map(s => renderShiftItem(s, inst))}
                                    {data.clinic.map(s => renderShiftItem(s, inst))}
                                  </div>
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </React.Fragment>
                ));
              })()}
              {/* On-call band: only rendered for departments that actually run on-call. */}
              {hasOnCall && (
              <tr className="bg-emerald-50/5 print-page-break">
                <td className="border-r border-slate-100 bg-emerald-50/10 p-4 align-middle text-center">
                  <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-[0.2em] [writing-mode:vertical-lr] rotate-180">On-Call</span>
                </td>
                {weekDates.map(dateStr => (
                  <td key={dateStr} className={`p-2 border-r border-slate-50 align-top last:border-r-0 ${isWeekendISO(dateStr) ? WEEKEND_CELL : 'bg-emerald-50/5'}`}>
                    <div className="flex flex-col border border-emerald-100 overflow-hidden bg-white shadow-sm rounded-md">
                      {(pivotedData[dateStr]?.onCall || []).map((s, i) => {
                        const isConsultant = rank0(s.employee);
                        return (
                          <div key={i} className={`flex items-center justify-between px-3 py-1.5 border-b border-emerald-50 last:border-0 transition-colors ${isConsultant ? 'bg-[#042f2e] text-white' : 'hover:bg-emerald-50/30'}`}>
                            <span className={`text-[9px] font-bold uppercase truncate w-1/2 leading-none ${isConsultant ? 'text-teal-100/70' : 'text-emerald-600'}`}>{roleName(rolesOf(schedule), s.role)}</span>
                            <span className={`text-[11px] font-bold text-right w-1/2 leading-none truncate ${isConsultant ? 'text-white' : 'text-emerald-900'}`}>{s.employee?.name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </td>
                ))}
              </tr>
              )}
              <tr className="bg-rose-50/5">
                <td className="border-r border-slate-100 bg-rose-50/10 p-4 align-middle text-center">
                  <span className="text-[10px] font-bold text-rose-600 uppercase tracking-[0.2em] [writing-mode:vertical-lr] rotate-180">Leave</span>
                </td>
                {weekDates.map(dateStr => (
                  <td key={dateStr} className={`p-2 border-r border-slate-50 align-top last:border-r-0 ${isWeekendISO(dateStr) ? WEEKEND_CELL : 'bg-rose-50/5'}`}>
                    <div className="flex flex-col gap-1">
                      {(pivotedData[dateStr]?.leave || []).map((entry, i) => (
                        <div key={i} className="flex items-center justify-between px-3 py-1.5 border-b border-rose-50 last:border-0 bg-white shadow-sm rounded-md transition-colors hover:bg-rose-50/30">
                          <span className="text-[11px] font-bold text-rose-900 leading-none truncate w-1/2">{entry.name}</span>
                          <div className="flex flex-col items-end w-1/2 min-w-0">
                            {entry.label && <span className="text-[9px] font-bold text-right leading-none truncate w-full text-rose-500">{entry.label}</span>}
                            <span className="text-[8px] font-bold text-right leading-none truncate w-full text-rose-300 uppercase tracking-tighter mt-0.5">{entry.type}</span>
                          </div>
                        </div>
                      ))}
                      {(!pivotedData[dateStr]?.leave || pivotedData[dateStr]?.leave.length === 0) && (
                        <div className="px-3 py-3 text-[11px] font-bold text-slate-300 text-center uppercase tracking-widest">NIL</div>
                      )}
                    </div>
                  </td>
                ))}
              </tr>
              {showFreeRows && (
              <tr className="bg-slate-50/40">
                <td className="border-r border-slate-100 bg-slate-100/40 p-4 align-middle text-center">
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.2em] [writing-mode:vertical-lr] rotate-180">Free</span>
                </td>
                {weekDates.map(dateStr => {
                  const am = freeStaff(schedule, dateStr, 'AM');
                  const pm = freeStaff(schedule, dateStr, 'PM');
                  const rows = [...am.map(n => ['FREE (AM)', n]), ...pm.map(n => ['FREE (PM)', n])];
                  return (
                    <td key={dateStr} className={`p-2 border-r border-slate-50 align-top last:border-r-0 ${isWeekendISO(dateStr) ? WEEKEND_CELL : 'bg-slate-50/30'}`}>
                      <div className="flex flex-col gap-1">
                        {rows.map(([tag, name], i) => (
                          <div key={i} className="flex items-center justify-between px-3 py-1.5 bg-white shadow-sm rounded-md">
                            <span className="text-[9px] font-bold uppercase leading-none text-slate-400 w-1/2 truncate">{tag}</span>
                            <span className="text-[11px] font-bold text-right leading-none text-slate-700 w-1/2 truncate">{name}</span>
                          </div>
                        ))}
                        {rows.length === 0 && (
                          <div className="px-3 py-3 text-[11px] font-bold text-slate-300 text-center uppercase tracking-widest">NIL</div>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
              )}
              {showFreeRows && (
              <tr className="bg-teal-50/10">
                <td className="border-r border-slate-100 bg-teal-50/20 p-4 align-middle text-center">
                  <span className="text-[10px] font-bold text-teal-600 uppercase tracking-[0.2em] [writing-mode:vertical-lr] rotate-180">Post-Call</span>
                </td>
                {weekDates.map(dateStr => {
                  const names = postCallStaff(schedule, dateStr);
                  return (
                    <td key={dateStr} className={`p-2 border-r border-slate-50 align-top last:border-r-0 ${isWeekendISO(dateStr) ? WEEKEND_CELL : 'bg-teal-50/5'}`}>
                      <div className="flex flex-col gap-1">
                        {names.map((name, i) => (
                          <div key={i} className="flex items-center justify-between px-3 py-1.5 bg-white shadow-sm rounded-md">
                            <span className="text-[9px] font-bold uppercase leading-none text-teal-400 w-1/2 truncate">POST-CALL</span>
                            <span className="text-[11px] font-bold text-right leading-none text-teal-700 w-1/2 truncate">{name}</span>
                          </div>
                        ))}
                        {names.length === 0 && (
                          <div className="px-3 py-3 text-[11px] font-bold text-slate-300 text-center uppercase tracking-widest">NIL</div>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
              )}
              {!showFreeRows && (
              <tr className="bg-slate-50/40">
                <td className="border-r border-slate-100 bg-slate-100/40 p-4 align-middle text-center">
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.2em] [writing-mode:vertical-lr] rotate-180">Off</span>
                </td>
                {weekDates.map(dateStr => {
                  // Staff with no clinical shift, no on-call and no leave that
                  // day: their derived rest day.
                  //
                  // This band used to be labelled "Unassigned", which conflated
                  // two different things. "Unassigned" describes a SLOT the
                  // solver could not fill; this list is of PEOPLE who are not
                  // working. Once a working-days cap is in force the distinction
                  // is real — the days it leaves free are off days by design,
                  // not coverage gaps.
                  const day = pivotedData[dateStr] || {};
                  const busy = new Set();
                  (day.clinical || []).forEach(s => { if (s.employee) busy.add(s.employee.name); });
                  (day.onCall || []).forEach(s => { if (s.employee) busy.add(s.employee.name); });
                  (day.leave || []).forEach(l => busy.add(l.name));
                  const offToday = (schedule?.employees || [])
                    // Second, independent leave check straight off the leave map.
                    // day.leave is the pivoter's view; if it ever regresses we
                    // must not paint OFF over somebody's annual leave.
                    .filter(e => !isOnLeaveByBlocks(e, dateStr))
                    .map(e => e.name)
                    .filter(name => !busy.has(name))
                    .sort((a, b) => a.localeCompare(b));
                  return (
                    <td key={dateStr} className={`p-2 border-r border-slate-50 align-top last:border-r-0 ${isWeekendISO(dateStr) ? WEEKEND_CELL : 'bg-slate-50/30'}`}>
                      <div className="flex flex-col gap-1">
                        {offToday.map((name, i) => (
                          <div key={i} className="px-3 py-1.5 border-b border-slate-100 last:border-0 bg-white shadow-sm rounded-md transition-colors hover:bg-slate-50">
                            <span className="text-[11px] font-bold text-slate-500 leading-none truncate block">{name}</span>
                          </div>
                        ))}
                        {offToday.length === 0 && (
                          <div className="px-3 py-3 text-[11px] font-bold text-slate-300 text-center uppercase tracking-widest">NIL</div>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderStaffLedger = () => {
    // Staff are listed in a single alphabetical order. Rank/other grouping was
    // removed because the previous implementation mis-grouped everyone into one
    // bucket (it matched on the hardcoded title string "Consultant" etc., which
    // no longer holds once a site renames ranks). A flat A-Z list is what's
    // wanted today.
    //
    // SEAM FOR FUTURE GROUPING: if a department later asks for grouped staff,
    // reintroduce it here by partitioning `staffList` on a real key (e.g.
    // emp.rank_override via the rank maps, NOT the title string) and emitting a
    // header row per group. The row body below is grouping-agnostic and can be
    // reused as-is.
    const staffList = (schedule?.employees || [])
      .filter(emp => {
        const name = emp.name.toLowerCase();
        return !searchQuery || name.includes(searchQuery.toLowerCase());
      })
      .sort((a, b) => a.name.localeCompare(b.name));

    // Group into ordered sections. In 'rank' mode this is a single ungrouped
    // section (renders as the existing flat A-Z list). In 'ward' mode staff are
    // grouped by anchor location, with a "No anchor" section last and multi-anchor
    // staff duplicated across their wards.
    const sections = groupStaff(staffList, staffGroupBy, schedule?.locations || [],
                                schedule?.locationNames || schedule?.location_names || {});
    const nDateCols = weekDates.length;

    return (
      <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden">
        {/* Staff grouping is a refinement of THIS view only, so its control
            lives here rather than in the top toolbar. Server-persisted via
            changeStaffGroupBy so dashboard + Excel export stay in step. */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50/40 no-print">
          <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Group staff by</span>
          <div className="w-[180px]">
            <SegmentedControl value={staffGroupBy} onChange={changeStaffGroupBy}
              options={[{ id: 'rank', label: 'Rank' }, { id: 'ward', label: 'Ward' }]} />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse table-fixed min-w-[1200px]">
            <thead>
              <tr className="bg-white border-b border-slate-200">
                <th className="w-48 p-4 border-r border-slate-100 sticky left-0 bg-white z-10 text-left text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                  Staff Member
                </th>
                {weekDates.map(dateStr => {
                  const d = new Date(dateStr);
                  const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                  return (
                    <th key={dateStr} className={`p-4 text-center border-r border-slate-100 last:border-r-0 ${isWeekend ? WEEKEND_HEADER : ''}`}>
                      <div className="flex flex-col items-center">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                          {d.toLocaleDateString('en-US', { weekday: 'short' })}
                        </span>
                        <span className="text-sm font-bold text-slate-900 tracking-tight">
                          {d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                        </span>
                        {/* Daily total: unique staff working clinical shifts that
                            day — the same figure the calendar view shows. */}
                        {uniqueStaffOnDate(dateStr) > 0 && (
                          <span className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold tabular-nums text-teal-700 bg-teal-50 border border-teal-100 rounded-full px-2 py-0.5" title="Total staff working this day (unique headcount; a cross-cover counts once)">
                            <Users className="w-2.5 h-2.5" />{uniqueStaffOnDate(dateStr)}
                          </span>
                        )}
                      </div>
                    </th>
                  );
                })}
                <th className="w-40 p-4 text-center border-l border-slate-200 bg-slate-50/40">
                  <div className="flex flex-col items-center">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                      {counterMetric === 'hours' ? 'Hours' : 'Shifts'}
                    </span>
                    <span className="text-[8px] font-bold uppercase tracking-widest text-slate-300">Wk · We · Total</span>
                    {/* Mirror of the Settings control, for whichever metric is
                        on display. Each metric has its own toggle and value, so
                        this reads and writes only the active one. */}
                    {(() => {
                      const cfg = leaveCounter[counterMetric];
                      if (!cfg) return null;
                      const unit = counterMetric === 'hours' ? 'h' : ' shift';
                      const plural = counterMetric === 'hours' ? 'h' : (cfg.value === 1 ? ' shift' : ' shifts');
                      return (
                        <button
                          type="button"
                          onClick={toggleLeaveForMetric}
                          title={cfg.enabled
                            ? `Each leave day counts as ${cfg.value}${unit}. Click to exclude leave.`
                            : `Leave is not counted. Click to include it in the ${counterMetric} total.`}
                          className={`mt-1.5 px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest transition-all ${
                            cfg.enabled
                              ? 'bg-rose-100 text-rose-700 hover:bg-rose-200'
                              : 'bg-slate-100 text-slate-400 hover:bg-slate-200 hover:text-slate-600'}`}
                        >
                          {cfg.enabled ? `incl. leave · ${cfg.value}${plural}` : 'excl. leave'}
                        </button>
                      );
                    })()}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {sections.map(section => (
                <React.Fragment key={section.key}>
                  {section.label && (
                    <tr className="bg-slate-100/70 border-b border-slate-200 print-page-break">
                      <td
                        colSpan={nDateCols + 2}
                        className="sticky left-0 z-10 bg-slate-100/70 px-4 py-2 text-[10px] font-black uppercase tracking-[0.2em] text-slate-500"
                      >
                        {section.label}
                      </td>
                    </tr>
                  )}
                  {section.staff.map(emp => {
                      const staffItems = pivotedData[emp.id] || [];
                      // Filter items for the current week only
                      const weekItems = staffItems.filter(item => {
                        const itemDate = (item.type === 'clinical' ? item.start.split('T')[0] : item.start);
                        return weekDates.includes(itemDate);
                      });

                      // Check for leave in the current week
                      const leave = leaveOf(emp);
                      const hasLeaveThisWeek = weekDates.some(d => onLeave(emp, d));

                      // If no clinical, no on-call, and no leave this week, skip the row.
                      //
                      // KEEP THIS. The test is per-WEEK, not per-day, so it
                      // cannot hide anyone who is merely off for a day or two —
                      // a working person always has at least one item in the
                      // week. It only drops people not rostered in this period
                      // at all (locums are hard-blocked from clinical shifts),
                      // whose seven OFF cells would be a noise row on every
                      // printed roster.
                      //
                      // Exception: if you searched for someone by name, you want
                      // to see them even if the answer is "not on this week".
                      if (weekItems.length === 0 && !hasLeaveThisWeek && !searchQuery) return null;

                      return (
                        <tr key={emp.name} className="border-b border-slate-100 last:border-b-slate-200 hover:bg-slate-50/40 transition-colors group">
                          <td className="p-4 border-r border-slate-200 sticky left-0 bg-white z-10 font-bold text-slate-700 text-xs shadow-[4px_0_8px_-4px_rgba(0,0,0,0.05)] group-hover:bg-slate-50 transition-colors">
                            {emp.name}
                          </td>
                          {weekDates.map(dateStr => {
                            const dayClinical = weekItems.filter(i => i.type === 'clinical' && i.start.split('T')[0] === dateStr);
                            const dayOnCall = weekItems.find(i => i.type === 'oncall' && i.start === dateStr);
                            
                            const amLeave = onLeave(emp, dateStr, 'AM');
                            const pmLeave = onLeave(emp, dateStr, 'PM');
                            const isOnLeave = amLeave || pmLeave;
                            // Point 4: leave label for this person+date.
                            const leaveLabel = leaveLabelOf(emp, dateStr);

                            // Codes this person actually works this week, so a
                            // clinic-only doctor still shows AM/PM and an ED one
                            // gains an N row -- no empty rows either way.
                            const personCodes = (() => {
                              const present = new Set(weekItems
                                .filter(i => i.type === 'clinical')
                                .map(i => codeOf(i)));
                              const used = orderedCodes.filter(c => present.has(c));
                              return used.length ? used : ['AM', 'PM'];
                            })();

                            const isCall = !!dayOnCall;
                            // A derived rest day: nothing clinical, no on-call,
                            // no leave. On-call counts as working, and leave is
                            // checked first above, so a half-day leave correctly
                            // shows LEAVE rather than OFF.
                            const isOffDay = !isOnLeave && dayClinical.length === 0 && !dayOnCall;
                            // cellBg is now neutral; highlighting is row-specific
                            const cellBg = 'bg-white';
                            const dividerColor = 'bg-slate-50';

                            const renderRowContent = (label, shift, leave, isCross = false) => {
                              // NOTE: the small `label` (shift code) badge that used
                              // to sit on the left was removed — in the shift branch it
                              // duplicated the prominent code text below, and in the
                              // leave/empty branches it was redundant. The on-call
                              // indicator (separate header row above) is unaffected.
                              if (leave) return (
                                <div className="flex items-center gap-3 h-9 px-3">
                                  <div className="flex flex-col min-w-0">
                                    {/* Point 4: show the leave type/label prominently, "LEAVE" as fallback. */}
                                    <span className="text-[9px] font-black uppercase tracking-wide text-rose-500/90 leading-none truncate">{leaveLabel || 'LEAVE'}</span>
                                    {leaveLabel && <span className="text-[7px] font-bold uppercase tracking-tighter text-rose-300 leading-none mt-0.5">Leave</span>}
                                  </div>
                                </div>
                              );
                              if (shift) return (
                                <div className="flex items-center gap-3 h-9 px-3">
                                  <div className="flex flex-col min-w-0">
                                    {/* Point 5: shift CODE prominent; ward location demoted below. */}
                                    <span className="text-[10px] font-black uppercase tracking-tight leading-none truncate text-slate-800">{codeOf(shift)}</span>
                                    <span className="text-[7px] font-bold uppercase tracking-tighter mt-0.5 text-slate-400 leading-none truncate">{slotLocationName(shift)}</span>
                                    {isCross && (
                                      <span className="text-[7px] font-black uppercase tracking-wider mt-0.5 text-amber-700 leading-none truncate" title="Cross-cover: covering this ward and another in the same hours">⚠ cross-cover</span>
                                    )}
                                    {/* Point 3: assignment label (e.g. Table 4), if present. */}
                                    {(shift.assignmentLabel || shift.assignment_label) && (
                                      <span className="text-[7px] font-bold uppercase tracking-tighter mt-0.5 text-teal-600 leading-none truncate">{shift.assignmentLabel || shift.assignment_label}</span>
                                    )}
                                  </div>
                                </div>
                              );
                              return (
                                <div className="flex items-center gap-3 h-9 px-3 opacity-20">
                                  <div className="w-1 h-1 rounded-full bg-slate-200"></div>
                                </div>
                              );
                            };

                            return (
                              // Leave (rose) wins over the weekend tint: it is per-PERSON
                              // information, whereas the weekend grey is column context.
                              // The header and neighbouring cells still carry the column.
                              <td key={dateStr} className={`p-0 border-r border-slate-100 align-top min-w-[180px] relative transition-all ${isOnLeave ? 'bg-rose-50/20' : (isWeekendISO(dateStr) ? WEEKEND_CELL : cellBg)}`}>
                                <div className="flex flex-col">
                                  {/* Dedicated On-Call Header Row (Highlighted if on call) */}
                                  <div className={`h-6 flex items-center px-3 border-b transition-colors ${isCall ? 'bg-[#042f2e] border-[#042f2e]' : 'bg-slate-50/30 border-transparent'}`}>
                                    {isCall ? (
                                      <div className="flex items-center gap-1.5">
                                        <div className="w-1 h-1 rounded-full bg-teal-400 shadow-[0_0_6px_rgba(45,212,191,0.8)]" />
                                        <span className="text-[8px] font-black text-teal-300 uppercase tracking-widest">
                                          CALL: {roleName(rolesOf(schedule), dayOnCall.role).replace('Senior Resident', 'SR').replace('Consultant', 'CONS')}
                                        </span>
                                      </div>
                                    ) : (
                                      <div className="w-full h-full flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                                        <span className="text-[7px] font-bold text-slate-300 uppercase tracking-widest">No Call</span>
                                      </div>
                                    )}
                                  </div>

                                  {isOffDay ? (
                                    // One day-level statement instead of a row
                                    // per shift code. Rendering OFF inside
                                    // renderRowContent would print "OFF / OFF"
                                    // in a single cell and assert a whole-day
                                    // fact from a half-day renderer.
                                    <div className="flex items-center justify-center h-9 px-3">
                                      <span className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-300">Off</span>
                                    </div>
                                  ) : personCodes.map((code, ci) => {
                                    // A staff member can now hold MORE THAN ONE
                                    // shift of the same code in a day (e.g. two MID
                                    // shifts at different wards). Render one row per
                                    // matching shift so every ward is shown; fall
                                    // back to a single empty slot when none.
                                    const codeShifts = dayClinical.filter(s => codeOf(s) === code);
                                    const rows = codeShifts.length ? codeShifts : [null];
                                    return rows.map((shift, si) => {
                                      // Leave is keyed on AM/PM half-days: a shift
                                      // uses its own bucket; an empty slot uses the
                                      // code's natural side (non-AM/PM -> PM).
                                      const bucket = shift ? bucketOf(shift) : (code === 'AM' ? 'AM' : 'PM');
                                      const leave = bucket === 'AM' ? amLeave : pmLeave;
                                      const first = ci === 0 && si === 0;
                                      return (
                                        <React.Fragment key={`${code}-${si}`}>
                                          {!first && <div className={`h-[1px] w-full ${dividerColor}`} />}
                                          {renderRowContent(code, shift, leave, codeShifts.length > 1)}
                                        </React.Fragment>
                                      );
                                    });
                                  })}
                                </div>
                              </td>
                            );
                          })}
                          {(() => {
                            // Counter over the visible window, across all this
                            // person's locations. All metrics computed; the
                            // global toggle selects which shows. Leave is folded
                            // into each metric under that metric's own toggle,
                            // and `lv` is the part of the total it accounts for.
                            const c = computeStaffCounters(weekItems, weekDates, {
                              leave,
                              leaveShiftsValue: leaveValueFor('shifts'),
                              leaveHoursValue: leaveValueFor('hours'),
                            });
                            const m = counterMetric === 'hours' ? c.hours : c.shifts;
                            const lv = counterMetric === 'hours' ? c.leaveHours : c.leaveShifts;
                            const fmt = (n) => (counterMetric === 'hours' ? `${n}h` : `${n}`);
                            return (
                              <td className="p-3 border-l border-slate-200 bg-slate-50/40 align-middle text-center">
                                <div className="flex items-baseline justify-center gap-1.5">
                                  <span className="text-sm font-black text-slate-800 tabular-nums">{fmt(m.total)}</span>
                                </div>
                                <div className="mt-0.5 text-[9px] font-bold text-slate-400 tabular-nums">
                                  <span title="Weekday">{fmt(m.weekday)}</span>
                                  <span className="text-slate-300"> · </span>
                                  <span className="text-terracotta" title="Weekend">{fmt(m.weekend)}</span>
                                </div>
                                {/* How much of the hours total came from leave.
                                    Without it the number is unauditable: 40h
                                    could be five worked days or three worked
                                    and two on leave, and a planner cannot tell
                                    which by looking. */}
                                {lv.total > 0 && (
                                  <div className="mt-0.5 text-[8px] font-black uppercase tracking-wider text-rose-500 tabular-nums"
                                       title={`${fmt(lv.total)} of this total is leave, at ${leaveCounter[counterMetric]?.value ?? '?'} per leave day`}>
                                    incl. {fmt(lv.total)} leave
                                  </div>
                                )}
                              </td>
                            );
                          })()}
                        </tr>
                      );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const isPublished = (schedule?.published_assignments && Object.keys(schedule.published_assignments).length > 0) || 
                      (schedule?.onCallRoster && schedule.onCallRoster.length > 0);

  return (
    <div className="animate-in fade-in space-y-6">
      {/* FLOATING IDENTITY */}
      <PageHeader icon={FileText} title="Final Published Roster" />

      {/* UNIFIED CONTROL CONTAINER — one wrapping row (Sep 2026 declutter):
          [view] [period] [search] ................... [download].
          The Week/Fortnight span choice lives in the period navigator's label
          menu; staff grouping moved into the Staff ledger's own header. */}
      <div className="no-print">
        <ControlBar>
          <ToggleGroup label="View" value={groupBy} onChange={setGroupBy}
            options={[{ id: 'date', label: 'Calendar', icon: Calendar }, { id: 'staff', label: 'Staff', icon: Users }]} />
          <ControlGroup label="Period" className="shrink-0">
            <PeriodNav
              startDate={weekRange?.start}
              endDate={weekRange?.end}
              onPrev={() => setWeekOffset(weekOffset - (rosterScope === 'fortnight' ? 2 : 1))}
              onNext={() => setWeekOffset(weekOffset + (rosterScope === 'fortnight' ? 2 : 1))}
              menu={[
                { id: 'week', label: 'Week view', sublabel: '7 days',
                  checked: rosterScope === 'week', onSelect: () => setRosterScope('week') },
                { id: 'fortnight', label: 'Fortnight view', sublabel: '14 days',
                  checked: rosterScope === 'fortnight', onSelect: () => setRosterScope('fortnight') },
              ]}
            />
          </ControlGroup>
          <SearchBox value={searchQuery} onChange={setSearchText} placeholder="Search staff..." />
          <EndGroup label="Export">
              <Dropdown label="Download" icon={Download} variant="secondary" align="right"
                triggerClassName="h-[36px] font-bold text-xs"
                items={[
                  { id: 'excel', label: 'Excel workbook', icon: FileSpreadsheet,
                    sublabel: rosterScope === 'fortnight' ? '14-day window' : '7-day window',
                    // Pass the FIRST DAY OF THE VISIBLE WINDOW, not just its
                    // length. Sending only the length left the server to guess
                    // the week from its own clock, which is why every download
                    // came back as the current week regardless of what was on
                    // screen. weekDates is already the on-screen window.
                    onSelect: () => exportToExcel(rosterScope === 'fortnight' ? 14 : 7, weekDates[0]) },
                  { id: 'pdf', label: 'PDF / Print', icon: FileText, onSelect: () => exportToPDF() },
                ]}
              />
          </EndGroup>
        </ControlBar>
      </div>

      {!isPublished ? (
        <Card className="py-24 text-center">
          <Calendar className="w-16 h-16 text-slate-200 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-700 mb-2">No Published Roster Found</h2>
          <p className="text-slate-500 mb-6 font-medium text-sm max-w-sm mx-auto">Once you finalize and publish a roster from the Management Console, the official operational ledger will be generated here.</p>
          <div className="flex justify-center gap-3">
            <Button onClick={onViewConsole} variant="secondary" icon={ArrowRight}>View Management Console</Button>
          </div>
        </Card>
      ) : (
        <div className="pb-20 animate-in fade-in duration-700">
          {groupBy === 'date' && renderWeeklyLedger()}
          {groupBy === 'staff' && renderStaffLedger()}
        </div>
      )}
    </div>
  );
};

export default FinalRosterTab;